import type { ClientForSelector } from '@src/clients/ClientSelectOption';
import {
  filterClientSelectOptions,
  getClientSelectOptionLabel,
  renderClientSelectOption,
} from '@src/clients/ClientSelectOption';
import { Autocomplete } from '@src/common/components/Autocomplete';
import { CircularProgress } from '@src/common/components/CircularProgress';
import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { TextInput } from '@src/common/components/TextInput';
import { Typography } from '@src/common/components/Typography';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { t } from 'i18next';
import { Fragment, useEffect, useState } from 'react';

const title = css(() => ({
  fontWeight: 500,
}));

interface ClientSelectionSectionProps {
  //This client might be an archived client in the system (aka not in activeClients)
  defaultClient?: ClientForSelector | string;
  activeClients: ClientForSelector[];
  isLoading: boolean;
  readonly?: boolean;
  onClientChange?: (client: ClientForSelector | null) => void;
  showTitle?: boolean;
}

export const ClientSelectionSection = ({
  defaultClient,
  activeClients,
  isLoading: loadingClients,
  readonly,
  onClientChange,
  showTitle = true,
}: ClientSelectionSectionProps) => {
  const [selectedClient, setSelectedClient] =
    useState<ClientForSelector | null>(
      getSelectedClientAtRender(defaultClient),
    );

  const selectClient = (client: ClientForSelector | null) => {
    setSelectedClient(client);
    onClientChange?.(client);
  };

  useEffect(() => {
    const clientToBeInitiallySelected = getClientToBeInitiallySelected(
      activeClients,
      defaultClient,
    );
    if (clientToBeInitiallySelected && !selectedClient) {
      setSelectedClient(clientToBeInitiallySelected);
      onClientChange?.(clientToBeInitiallySelected);
    }
  }, [activeClients, selectedClient, onClientChange, defaultClient]);

  return (
    <ClientSelector
      readonly={readonly}
      loadingClients={loadingClients}
      clients={activeClients}
      selectedClient={selectedClient}
      selectClient={selectClient}
      showTitle={showTitle}
    />
  );
};

type ClientSelectorProps = {
  readonly: undefined | boolean;
  loadingClients: boolean;
  clients: ClientForSelector[];
  selectedClient: ClientForSelector | null;
  selectClient: (client: ClientForSelector | null) => void;
  showTitle?: boolean;
};

export const ClientSelector = (props: ClientSelectorProps) => {
  return (
    <Stack direction="column" gap={theme.spacing['3']}>
      {props.showTitle && (
        <>
          <Typography className={title}>{t('client')}</Typography>
          <Divider />
        </>
      )}
      <Autocomplete
        data-testid="client-autocomplete"
        readOnly={props.readonly}
        disabled={props.readonly}
        isOptionEqualToValue={(client, value) =>
          (client as ClientForSelector).id === (value as ClientForSelector).id
        }
        getOptionLabel={getClientSelectOptionLabel}
        filterOptions={filterClientSelectOptions}
        renderOption={renderClientSelectOption}
        options={props.clients}
        loading={props.loadingClients}
        value={props.selectedClient}
        onChange={(_, value) => {
          if (!props.readonly) {
            props.selectClient(value as ClientForSelector | null);
          }
        }}
        renderInput={(params) => (
          <TextInput
            {...params}
            label={t('client')}
            disabled={props.readonly}
            slotProps={{
              input: {
                ...params.InputProps,
                readOnly: props.readonly,
                endAdornment: (
                  <Fragment>
                    {props.loadingClients ? (
                      <CircularProgress size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </Fragment>
                ),
              },
            }}
          />
        )}
      />
    </Stack>
  );
};

const getSelectedClientAtRender = (
  defaultClient: ClientForSelector | string | undefined,
) => (typeof defaultClient === 'string' ? null : (defaultClient ?? null));

const getClientToBeInitiallySelected = (
  activeClients: ClientForSelector[],
  defaultClient?: ClientForSelector | string,
): ClientForSelector | null => {
  if (defaultClient === undefined) {
    return null;
  }
  if (typeof defaultClient !== 'string') {
    return defaultClient;
  }
  if (activeClients.length > 0) {
    const foundMatchingActiveClient = activeClients.find(
      (client) => client.id === defaultClient,
    );
    if (foundMatchingActiveClient) {
      return foundMatchingActiveClient;
    }
  }
  return null;
};
